import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingDown, Building2, ShieldCheck, ArrowRight } from "lucide-react";

const MARGIN_OPTIONS = [
  { pct: 15, label: "15% (low-end agency)" },
  { pct: 20, label: "20% (typical)" },
  { pct: 25, label: "25% (specialist)" },
];

function EUR(n) {
  return `€${(Math.round(n * 100) / 100).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function FeeComparisonWidget() {
  const [workers, setWorkers] = useState(3);
  const [hours, setHours] = useState(8);
  const [rate, setRate] = useState(22);
  const [margin, setMargin] = useState(20);

  const calc = useMemo(() => {
    const w = Math.max(1, Number(workers) || 0);
    const h = Math.max(1, Number(hours) || 0);
    const r = Math.max(1, Number(rate) || 0);
    const m = Math.max(0, Number(margin) || 0);

    const bookingValue = w * h * r;
    const perHourFee = w * h * 1.0;
    const pctFee = bookingValue * 0.03;
    const secuFee = Math.min(perHourFee, pctFee);
    const secuMethod = perHourFee <= pctFee ? "€1/hr per worker" : "3% of booking value";
    const secuEffectivePct = bookingValue > 0 ? (secuFee / bookingValue) * 100 : 0;

    // Traditional agency: margin is applied on top of the booking value (client pays booking + margin)
    // i.e. agencyCost = bookingValue * (margin/100)
    const agencyCost = bookingValue * (m / 100);
    const savings = Math.max(0, agencyCost - secuFee);
    const savingsPct = agencyCost > 0 ? (savings / agencyCost) * 100 : 0;

    return { bookingValue, perHourFee, pctFee, secuFee, secuMethod, secuEffectivePct, agencyCost, savings, savingsPct };
  }, [workers, hours, rate, margin]);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] shadow-sm overflow-hidden" data-testid="fee-comparison-widget">
      <div className="bg-navy-900 text-white px-6 py-5">
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-1 flex items-center gap-2">
          <TrendingDown className="w-3.5 h-3.5" /> How your fee compares
        </div>
        <h3 className="text-2xl font-outfit font-bold tracking-tight">Live side-by-side for the same shift.</h3>
        <p className="text-slate-300 text-sm mt-2 font-manrope max-w-2xl">
          Enter a real shift. See what a traditional agency would typically charge in margin, and what SecuSource charges as a transparent introduction fee.
        </p>
      </div>

      <div className="p-6 grid lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-2 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="font-manrope text-xs">Workers</Label>
              <Input
                type="number" min={1}
                value={workers}
                onChange={(e) => setWorkers(e.target.value)}
                data-testid="fcw-workers"
                className="text-white bg-white/5 border-white/15 placeholder:text-white/40"
              />
            </div>
            <div>
              <Label className="font-manrope text-xs">Hours</Label>
              <Input
                type="number" min={1} step="0.5"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                data-testid="fcw-hours"
                className="text-white bg-white/5 border-white/15 placeholder:text-white/40"
              />
            </div>
            <div>
              <Label className="font-manrope text-xs">Rate €/hr</Label>
              <Input
                type="number" min={1} step="0.5"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                data-testid="fcw-rate"
                className="text-white bg-white/5 border-white/15 placeholder:text-white/40"
              />
            </div>
          </div>

          <div>
            <Label className="font-manrope text-xs">Typical agency margin</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {MARGIN_OPTIONS.map((m) => (
                <button
                  key={m.pct}
                  type="button"
                  onClick={() => setMargin(m.pct)}
                  data-testid={`fcw-margin-${m.pct}`}
                  className={`px-2 py-2 rounded-md text-xs font-manrope font-semibold border transition ${
                    margin === m.pct
                      ? "bg-navy-900 text-white border-navy-900"
                      : "bg-[#0A0A0A] text-white border-white/10 hover:border-brand"
                  }`}
                >
                  {m.pct}%
                </button>
              ))}
            </div>
            <div className="text-[11px] text-white/60 mt-1 font-manrope">
              Industry range: 15–25%. Adjust to match the quotes you typically receive.
            </div>
          </div>

          <div className="rounded-md bg-[#0E0E0E] border border-white/10 p-3">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-outfit font-bold">Shift value</div>
            <div className="text-2xl font-outfit font-bold text-white" data-testid="fcw-booking-value">{EUR(calc.bookingValue)}</div>
            <div className="text-xs text-white/60 font-manrope">
              {workers} × {hours}h × €{rate}/hr
            </div>
          </div>
        </div>

        {/* Comparison */}
        <div className="lg:col-span-3 grid sm:grid-cols-2 gap-3">
          {/* Agency card */}
          <div className="rounded-xl p-5 border border-white/10 bg-[#0E0E0E] flex flex-col" data-testid="fcw-agency-card">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-md bg-slate-200 text-white/85 flex items-center justify-center">
                <Building2 className="w-4 h-4" />
              </div>
              <div className="font-outfit font-semibold text-white">Traditional Agency</div>
            </div>
            <div className="text-xs text-white/60 font-manrope mb-3">Estimated margin at {margin}% on this shift</div>
            <div className="text-3xl font-outfit font-bold text-white/85" data-testid="fcw-agency-cost">{EUR(calc.agencyCost)}</div>
            <div className="text-xs text-white/60 mt-1 font-manrope">≈ {margin.toFixed(1)}% of booking value</div>
            <div className="mt-auto pt-3 text-[11px] text-white/60 font-manrope">
              Typical placement fee absorbed into the rate charged to you.
            </div>
          </div>

          {/* SecuSource card */}
          <div className="rounded-xl p-5 border-2 border-brand bg-gradient-to-br from-[#1a0f04] via-[#0f0a06] to-[#141414] flex flex-col relative" data-testid="fcw-secu-card">
            <div className="absolute -top-2 right-3 text-[10px] uppercase tracking-widest bg-brand text-white px-2 py-0.5 rounded-full font-outfit font-bold">
              SecuSource
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-md bg-brand text-white flex items-center justify-center">
                <ShieldCheck className="w-4 h-4" strokeWidth={2.4} />
              </div>
              <div className="font-outfit font-semibold text-white">SecuSource Fee</div>
            </div>
            <div className="text-xs text-white/70 font-manrope mb-3">
              {calc.secuMethod} — automatically applied (whichever is lower)
            </div>
            <div className="text-3xl font-outfit font-bold text-brand" data-testid="fcw-secu-cost">{EUR(calc.secuFee)}</div>
            <div className="text-xs text-white/70 mt-1 font-manrope">≈ {calc.secuEffectivePct.toFixed(2)}% of booking value</div>
            <div className="mt-auto pt-3 text-[11px] text-white/70 font-manrope">
              One transparent introduction fee. No subscriptions or listing fees.
            </div>
          </div>

          {/* Savings summary */}
          <div className="sm:col-span-2 rounded-xl p-5 bg-navy-900 text-white flex flex-wrap items-center justify-between gap-4" data-testid="fcw-savings-card">
            <div>
              <div className="text-xs uppercase tracking-widest text-gold-400 font-outfit font-bold flex items-center gap-1">
                <TrendingDown className="w-3.5 h-3.5" /> You keep
              </div>
              <div className="text-4xl font-outfit font-bold text-gold-400" data-testid="fcw-savings-amount">{EUR(calc.savings)}</div>
              <div className="text-xs text-white/50 mt-1 font-manrope">per shift, vs. a {margin}% agency margin</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-white/50 font-outfit font-bold">Saving vs. agency</div>
              <div className="text-3xl font-outfit font-bold" data-testid="fcw-savings-pct">
                {calc.savingsPct.toFixed(0)}%
              </div>
              <div className="text-[11px] text-white/50 mt-1 font-manrope max-w-xs">
                Recurring per booking. Multiply across every deployment for the real annual impact.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#0E0E0E] px-6 py-4 flex flex-wrap justify-between items-center gap-3">
        <div className="text-xs text-white/60 font-manrope">
          Agency comparison is illustrative — based on standard 15–25% industry margin range.
        </div>
        <a href="/pricing" className="text-brand font-manrope font-semibold text-sm flex items-center gap-1 hover:underline">
          Full pricing details <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
